import React from "react";
import { Link, NavLink } from "react-router-dom";
import LanguageSelector from "./LanguageSelector";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import { ShoppingBag, User, Menu, LogOut, Home as HomeIcon } from "lucide-react";
import { Button } from "../components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "../components/ui/sheet";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

const NAV_ITEMS = [
  { to: "/b2b/anagrafica", label: "Anagrafica", testid: "b2b-nav-anagrafica" },
  { to: "/b2b/lots/sell", label: "Vendi lotto", testid: "b2b-nav-sell-lot" },
  { to: "/b2b/lots/marketplace", label: "Acquista lotti", testid: "b2b-nav-marketplace" },
  { to: "/b2b/lots/mine", label: "I miei lotti", testid: "b2b-nav-my-lots" },
  { to: "/b2b/wines", label: "Catalogo Amazonico", testid: "b2b-nav-wines" },
  { to: "/b2b/marketing", label: "Marketing", testid: "b2b-nav-marketing" },
  { to: "/b2b/auctions/propose", label: "Aste", testid: "b2b-nav-auctions" },
  { to: "/b2b/sommelier", label: "AI Sommelier", testid: "b2b-nav-sommelier" },
  { to: "/b2b/quotes", label: "Preventivi", testid: "b2b-nav-quotes" },
];

export default function B2BNavbar() {
  const { user, logout } = useAuth();
  const { count, setOpen } = useCart();
  return (
    <header data-testid="b2b-navbar" className="sticky top-0 z-40 bg-[hsl(var(--background))]/90 backdrop-blur-md border-b border-[hsl(var(--border))]">
      <div className="container-xl h-16 md:h-20 flex items-center justify-between gap-3">
        <Link to="/b2b/dashboard" data-testid="b2b-logo" className="flex items-center shrink-0">
          <img src="/brand/logo.png" alt="Amazonico 3000" className="h-10 md:h-12 w-auto" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-4 xl:gap-5 text-[13px]">
          {NAV_ITEMS.map((i) => (
            <NavLink key={i.to} to={i.to} data-testid={i.testid}
              className={({ isActive }) => isActive ? "text-[hsl(var(--accent))]" : "text-foreground/80 hover:text-[hsl(var(--accent))]"}>
              {i.label}
            </NavLink>
          ))}
          <Link to="/" data-testid="b2b-nav-consumer" className="text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5">← Sito principale</Link>
        </nav>

        <div className="flex items-center gap-1.5 md:gap-2">
          <Link to="/" data-testid="b2b-back-home-btn" className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[hsl(var(--accent))]/40 text-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))] hover:text-black transition-colors text-xs uppercase tracking-wider font-semibold">
            <HomeIcon className="h-3.5 w-3.5" />
            <span className="hidden md:inline">Sito principale</span>
          </Link>
          <div className="hidden sm:block"><LanguageSelector /></div>
          <button data-testid="b2b-cart-btn" onClick={() => setOpen(true)} className="relative inline-flex items-center justify-center h-10 w-10 md:h-11 md:w-11 rounded-full bg-[hsl(var(--accent))] text-black hover:bg-[hsl(var(--accent))]/90 transition-colors shadow-sm">
            <ShoppingBag className="h-5 w-5" strokeWidth={1.75} />
            {count > 0 && (<span className="absolute -top-1 -right-1 bg-[hsl(var(--primary))] text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center font-bold border-2 border-[hsl(var(--background))]">{count}</span>)}
          </button>
          {user ? (
            <>
              <Link to="/area-utente" data-testid="b2b-nav-account" className="hidden sm:inline-flex p-2 hover:bg-[hsl(var(--muted))] rounded-md"><User className="h-5 w-5" /></Link>
              <button
                data-testid="b2b-logout"
                onClick={logout}
                className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border text-foreground/80 hover:bg-destructive hover:text-white hover:border-destructive transition-colors text-sm font-semibold"
              >
                <LogOut className="h-3.5 w-3.5" />
                Esci
              </button>
            </>
          ) : (
            <Link to="/login" data-testid="b2b-nav-login" className="hidden sm:inline-flex">
              <Button size="sm" className="bg-[hsl(var(--accent))] text-black hover:bg-[hsl(var(--accent))]/90 rounded-sm">Accedi</Button>
            </Link>
          )}

          {/* Mobile menu */}
          <Sheet>
            <SheetTrigger asChild>
              <button data-testid="b2b-mobile-menu-btn" className="lg:hidden p-2.5 rounded-sm hover:bg-[hsl(var(--muted))]" aria-label="Menu">
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px]">
              <VisuallyHidden><SheetTitle>Menu B2B</SheetTitle></VisuallyHidden>
              <div className="mt-8 flex flex-col gap-1">
                {NAV_ITEMS.map((i) => (
                  <Link key={i.to} to={i.to} data-testid={`mobile-${i.testid}`} className="py-2.5 text-base font-medium border-b border-border">
                    {i.label}
                  </Link>
                ))}
                <Link to="/" data-testid="mobile-b2b-nav-consumer" className="py-2.5 text-base text-muted-foreground border-b border-border inline-flex items-center gap-1.5">
                  ← Sito principale Amazonico
                </Link>
                <div className="py-2.5 border-b border-border"><LanguageSelector /></div>
                {user ? (
                  <>
                    <Link to="/area-utente" className="py-2.5 text-base">Area utente</Link>
                    <button
                      data-testid="b2b-mobile-logout"
                      onClick={logout}
                      className="mt-3 w-full inline-flex items-center justify-center gap-2 py-3 text-base font-semibold rounded-sm border border-border text-foreground hover:bg-destructive hover:text-white hover:border-destructive transition-colors"
                    >
                      <LogOut className="h-4 w-4" />
                      Esci dall'account
                    </button>
                  </>
                ) : (
                  <Link to="/login" data-testid="b2b-mobile-login" className="py-2.5 text-base font-semibold text-[hsl(var(--accent))]">Accedi</Link>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
